import {
  playerManifestSchema,
  requiredMedia,
  type PlayerManifest,
  type SyncState,
} from '@signage/shared';
import type {
  ClockAdapter,
  DatabaseAdapter,
  FileSystemAdapter,
  LoggerAdapter,
} from './adapters.js';
import { ApiError, type PlayerApiClient } from './api-client.js';
import { CacheManager } from './cache-manager.js';
import { DownloadManager } from './download-manager.js';

export interface SyncProgress {
  state: SyncState;
  contentVersion: string | null;
  filesTotal: number;
  filesCompleted: number;
  bytesTotal: number;
  bytesCompleted: number;
  message?: string;
}

export interface SyncResult {
  outcome: 'up-to-date' | 'activated' | 'failed' | 'skipped' | 'cancelled';
  contentVersion: string | null;
  activeContentVersion: string | null;
  filesDownloaded: number;
  bytesDownloaded: number;
  message?: string;
  errors?: { mediaId?: string; message: string }[];
}

export interface SyncEngineOptions {
  cacheDir: string;
  maxCacheBytes?: number;
  downloadConcurrency?: number;
  onProgress?: (progress: SyncProgress) => void;
  /** Called after a new manifest becomes active. */
  onActivated?: (manifest: PlayerManifest) => void;
  /** Report progress to the server; failures here never abort a sync. */
  reportStatus?: boolean;
  /**
   * This player's release. A manifest is stored as this release parsed it, which drops fields
   * it does not know; after an upgrade the same content version must be fetched again, or the
   * new release would keep playing the old release's reading of it.
   */
  playerVersion?: string;
}

const ACTIVE_VERSION_KEY = 'activeContentVersion';
const MANIFEST_PLAYER_KEY = 'manifestPlayerVersion';

/**
 * The synchronization engine.
 *
 * Contract (see docs/PLAYER_PROTOCOL.md):
 *   1. Compare the server's content version with the active one. Equal ⇒ nothing to do.
 *   2. Fetch the new manifest and store it as *pending*. The active manifest keeps playing.
 *   3. Diff required media by sha256 and download only what is missing or changed.
 *   4. Verify every required file exists with the right size and checksum.
 *   5. Only when everything verifies, atomically promote pending → active.
 *   6. Garbage-collect files that nothing needs any more.
 *
 * A failure at any step before (5) leaves the previous manifest active and playing. That is
 * the central reliability guarantee of the product: an update can never blank a screen.
 */
export class SyncEngine {
  private readonly cache: CacheManager;
  private readonly downloads: DownloadManager;
  private running = false;
  private cancelRequested = false;

  constructor(
    private readonly fs: FileSystemAdapter,
    private readonly db: DatabaseAdapter,
    private readonly api: PlayerApiClient,
    private readonly clock: ClockAdapter,
    private readonly logger: LoggerAdapter,
    private readonly options: SyncEngineOptions,
  ) {
    this.cache = new CacheManager(fs, db, clock, logger, options.cacheDir);
    this.downloads = new DownloadManager(fs, api, clock, logger, {
      cacheDir: options.cacheDir,
      concurrency: options.downloadConcurrency ?? 2,
      shouldCancel: () => this.cancelRequested,
      onProgress: undefined,
    });
  }

  get cacheManager(): CacheManager {
    return this.cache;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** Ask a running sync to stop at the next safe point. */
  cancel(): void {
    if (this.running) this.cancelRequested = true;
  }

  /* ------------------------------------------------------------ manifest state */

  getActiveManifest(): PlayerManifest | null {
    const stored = this.db.getManifest('active');
    if (!stored) return null;
    return this.parseStored(stored.document, stored.contentVersion);
  }

  getPendingManifest(): PlayerManifest | null {
    const stored = this.db.getManifest('pending');
    if (!stored) return null;
    return this.parseStored(stored.document, stored.contentVersion);
  }

  getActiveContentVersion(): string | null {
    return this.db.getSetting(ACTIVE_VERSION_KEY);
  }

  private parseStored(document: string, contentVersion: string): PlayerManifest | null {
    try {
      const parsed = playerManifestSchema.safeParse(JSON.parse(document));
      if (!parsed.success) {
        this.logger.error('Stored manifest is invalid; ignoring it', { contentVersion });
        return null;
      }
      return parsed.data;
    } catch (err) {
      this.logger.error('Stored manifest could not be parsed', {
        contentVersion,
        error: (err as Error).message,
      });
      return null;
    }
  }

  private emit(progress: SyncProgress): void {
    this.options.onProgress?.(progress);
    if (this.options.reportStatus !== false) {
      void this.api
        .reportSyncStatus({
          state: progress.state,
          contentVersion: progress.contentVersion,
          activeContentVersion: this.getActiveContentVersion(),
          filesTotal: progress.filesTotal,
          filesCompleted: progress.filesCompleted,
          bytesTotal: progress.bytesTotal,
          bytesCompleted: progress.bytesCompleted,
          message: progress.message ?? null,
        })
        .catch(() => {
          /* status reporting is advisory; never let it affect the sync */
        });
    }
  }

  /* --------------------------------------------------------------------- boot */

  /**
   * Startup validation. Ensures the active manifest is playable from local storage before the
   * player starts; if a required file has vanished the manifest is still kept (the player will
   * skip unplayable items) and a resync is requested.
   */
  async validateOnBoot(): Promise<{
    manifest: PlayerManifest | null;
    missingFiles: number;
    repaired: boolean;
  }> {
    await this.fs.ensureDir(this.options.cacheDir);
    const manifest = this.getActiveManifest();
    await this.cache.reconcile(manifest);

    if (!manifest) return { manifest: null, missingFiles: 0, repaired: false };

    const { missing } = await this.cache.missingFor(manifest);
    if (missing.length > 0) {
      this.logger.warn('Active manifest references files that are not cached', {
        missing: missing.length,
      });
    }
    return { manifest, missingFiles: missing.length, repaired: false };
  }

  /* --------------------------------------------------------------------- sync */

  /**
   * Runs one synchronization pass.
   * `serverVersion` may be supplied from a heartbeat to avoid an extra round trip.
   */
  async sync(options: { serverVersion?: string; force?: boolean } = {}): Promise<SyncResult> {
    if (this.running) {
      return {
        outcome: 'skipped',
        contentVersion: null,
        activeContentVersion: this.getActiveContentVersion(),
        filesDownloaded: 0,
        bytesDownloaded: 0,
        message: 'A synchronization is already running',
      };
    }
    this.running = true;
    this.cancelRequested = false;

    const activeVersion = this.getActiveContentVersion();
    try {
      const serverVersion = options.serverVersion ?? (await this.api.contentVersion());

      const storedBy = this.db.getSetting(MANIFEST_PLAYER_KEY);
      const sameRelease = !this.options.playerVersion || storedBy === this.options.playerVersion;
      if (!sameRelease) {
        this.logger.info('Player release changed; fetching the manifest again', {
          from: storedBy,
          to: this.options.playerVersion,
        });
      }
      if (!options.force && sameRelease && serverVersion === activeVersion) {
        // Even when versions match, confirm the files are actually present. A device whose
        // storage was wiped must recover rather than sit "up to date" with nothing to play.
        const active = this.getActiveManifest();
        if (active) {
          const { missing } = await this.cache.missingFor(active);
          if (missing.length === 0) {
            this.emit({
              state: 'synced',
              contentVersion: serverVersion,
              filesTotal: 0,
              filesCompleted: 0,
              bytesTotal: 0,
              bytesCompleted: 0,
            });
            return {
              outcome: 'up-to-date',
              contentVersion: serverVersion,
              activeContentVersion: activeVersion,
              filesDownloaded: 0,
              bytesDownloaded: 0,
            };
          }
          this.logger.warn('Version matches but cached files are missing; re-syncing', {
            missing: missing.length,
          });
        }
      }

      this.logger.info('Starting synchronization', { from: activeVersion, to: serverVersion });

      /* ------------------------- 1. fetch the manifest ------------------------- */
      const manifest = await this.api.manifest();

      /* ------------------ 2. store it as pending (not active) ------------------ */
      // The same content read again (after an upgrade) replaces the active copy in place; it
      // needs the same files, and the screen must never be left without an active manifest.
      const reread = manifest.contentVersion === activeVersion;
      this.db.saveManifest({
        contentVersion: manifest.contentVersion,
        state: reread ? 'active' : 'pending',
        document: JSON.stringify(manifest),
        savedAt: this.clock.now(),
        activatedAt: reread ? this.clock.now() : null,
      });

      /* ------------------------ 3. diff and download -------------------------- */
      const wanted = requiredMedia(manifest);
      const { missing } = await this.cache.missingFor(manifest);
      const bytesTotal = missing.reduce((sum, m) => sum + m.sizeBytes, 0);

      this.emit({
        state: 'syncing',
        contentVersion: manifest.contentVersion,
        filesTotal: missing.length,
        filesCompleted: 0,
        bytesTotal,
        bytesCompleted: 0,
        message: `Downloading ${missing.length} file(s)`,
      });

      let bytesDownloaded = 0;
      const failures: { mediaId?: string; message: string }[] = [];

      if (missing.length > 0) {
        await this.downloads.cleanupPartials(new Set(wanted.map((m) => m.sha256)));
        const result = await this.downloads.downloadAll(missing);

        if (result.cancelled) {
          this.emit({
            state: 'idle',
            contentVersion: manifest.contentVersion,
            filesTotal: missing.length,
            filesCompleted: result.completed.length,
            bytesTotal,
            bytesCompleted: bytesDownloaded,
            message: 'Synchronization cancelled',
          });
          return {
            outcome: 'cancelled',
            contentVersion: manifest.contentVersion,
            activeContentVersion: activeVersion,
            filesDownloaded: result.completed.length,
            bytesDownloaded,
          };
        }

        for (const done of result.completed) {
          bytesDownloaded += done.bytesDownloaded;
          this.cache.record({
            sha256: done.media.sha256,
            mediaId: done.media.mediaId,
            path: done.path,
            sizeBytes: done.media.sizeBytes,
            mimeType: done.media.mimeType,
            verified: true,
          });
        }
        for (const failure of result.failed) {
          failures.push({ mediaId: failure.media.mediaId, message: failure.message });
        }

        this.emit({
          state: 'syncing',
          contentVersion: manifest.contentVersion,
          filesTotal: missing.length,
          filesCompleted: result.completed.length,
          bytesTotal,
          bytesCompleted: bytesDownloaded,
        });
      }

      /* ---------------- 4. verify everything required is present --------------- */
      const { missing: stillMissing } = await this.cache.missingFor(manifest);
      if (stillMissing.length > 0) {
        const message = `${stillMissing.length} required file(s) could not be downloaded; keeping the previous content`;
        this.logger.error(message, {
          contentVersion: manifest.contentVersion,
          missing: stillMissing.map((m) => m.mediaId).slice(0, 10),
        });
        this.emit({
          state: 'failed',
          contentVersion: manifest.contentVersion,
          filesTotal: missing.length,
          filesCompleted: missing.length - stillMissing.length,
          bytesTotal,
          bytesCompleted: bytesDownloaded,
          message,
        });
        return {
          outcome: 'failed',
          contentVersion: manifest.contentVersion,
          activeContentVersion: activeVersion,
          filesDownloaded: missing.length - stillMissing.length,
          bytesDownloaded,
          message,
          errors: failures.length > 0 ? failures : stillMissing.map((m) => ({
            mediaId: m.mediaId,
            message: 'File missing after download',
          })),
        };
      }

      /* ------------------------ 5. atomic activation --------------------------- */
      this.db.transaction(() => {
        this.db.activateManifest(manifest.contentVersion);
        this.db.setSetting(ACTIVE_VERSION_KEY, manifest.contentVersion);
        if (this.options.playerVersion) this.db.setSetting(MANIFEST_PLAYER_KEY, this.options.playerVersion);
      });

      // Drop superseded manifests, keeping the one that is now active.
      for (const stored of this.db.listManifests()) {
        if (stored.contentVersion !== manifest.contentVersion) {
          this.db.deleteManifest(stored.contentVersion);
        }
      }

      this.logger.info('New content activated', {
        contentVersion: manifest.contentVersion,
        files: wanted.length,
        downloaded: missing.length,
      });
      this.options.onActivated?.(manifest);

      /* --------------------------- 6. housekeeping ---------------------------- */
      const protectedHashes = this.cache.hashesFor(manifest);
      await this.cache.collectGarbage(protectedHashes, {
        maxCacheBytes: this.options.maxCacheBytes ?? manifest.settings.maxCacheBytes,
      });
      await this.downloads.cleanupPartials(protectedHashes);

      this.emit({
        state: 'synced',
        contentVersion: manifest.contentVersion,
        filesTotal: missing.length,
        filesCompleted: missing.length,
        bytesTotal,
        bytesCompleted: bytesDownloaded,
      });

      return {
        outcome: 'activated',
        contentVersion: manifest.contentVersion,
        activeContentVersion: manifest.contentVersion,
        filesDownloaded: missing.length,
        bytesDownloaded,
      };
    } catch (err) {
      const error = err as ApiError;
      const message = error.message ?? 'Synchronization failed';
      // An auth failure means the screen was unpaired or its token revoked. Playback of the
      // already-downloaded content continues; the caller decides whether to re-pair.
      this.logger.error('Synchronization failed', {
        error: message,
        status: (error as ApiError).status,
      });
      this.emit({
        state: 'failed',
        contentVersion: null,
        filesTotal: 0,
        filesCompleted: 0,
        bytesTotal: 0,
        bytesCompleted: 0,
        message,
      });
      return {
        outcome: 'failed',
        contentVersion: null,
        activeContentVersion: activeVersion,
        filesDownloaded: 0,
        bytesDownloaded: 0,
        message,
      };
    } finally {
      this.running = false;
      this.cancelRequested = false;
    }
  }

  /** Local file path for a media id in the active manifest, or null when unavailable. */
  localPathFor(mediaId: string, manifest?: PlayerManifest | null): string | null {
    const active = manifest ?? this.getActiveManifest();
    if (!active) return null;
    const media = active.media.find((m) => m.mediaId === mediaId);
    if (!media) return null;
    const path = this.cache.pathFor(media.sha256);
    const entry = this.db.getCacheEntry(media.sha256);
    if (!entry) return null;
    this.cache.markUsed(media.sha256);
    return path;
  }

  /** Clears everything so the next sync starts from scratch (a "clear cache" command). */
  async clearCache(): Promise<void> {
    const entries = this.db.listCacheEntries();
    for (const entry of entries) {
      await this.fs.remove(this.cache.pathFor(entry.sha256)).catch(() => {});
      this.db.deleteCacheEntry(entry.sha256);
    }
    await this.downloads.cleanupPartials(new Set());
    this.logger.warn('Local media cache cleared', { removed: entries.length });
  }
}
